import React from 'react';
import { createBrowserRouter, RouteObject, RouterProvider } from 'react-router-dom';

import { rootRoutes } from '@/app/appRoutes';
import { BetaFeatureRoute } from '@/app/providers/AppRouter/BetaFeatureRoute';
import { DefaultPathRoute } from '@/app/providers/AppRouter/DefaultPathRoute';
import { PermissionRoute } from '@/app/providers/AppRouter/PermissionRoute';
import { RouteErrorElement } from '@/app/providers/AppRouter/RouteErrorElement';
import { BetaFeatureCode } from '@/entities/beta-feature';

import { RouteConfig } from '@/shared/lib/ts-router';

/**
 * Экран ошибки маршрутов страниц: они вложены в маршрут каркаса приложения, поэтому падение
 * страницы показывается в области содержимого, не снимая левое меню и верхнюю панель.
 * Назначается всем вложенным маршрутам по умолчанию — иначе ошибка дошла бы до маршрута
 * каркаса и забрала бы навигацию вместе с собой.
 */
const pageErrorElement = <RouteErrorElement isEmbedded />;

/**
 * Бета-функция группы распространяется на её маршруты: закрытый раздел закрыт целиком, а не
 * только своим корнем — иначе адрес вложенной страницы отвечал бы в обход выключенной функции.
 */
const transformRoutes = (
  routes: readonly RouteConfig[],
  isNested = false,
  parentBetaFeature?: BetaFeatureCode,
): RouteObject[] => {
  return routes.flatMap((route) => {
    const { path, element, errorElement, getDefaultPath, children, viewPermission } = route;
    const betaFeature = route.betaFeature ?? parentBetaFeature;

    let routeElement = element;
    const routeErrorElement = errorElement ?? (isNested ? pageErrorElement : undefined);

    if (viewPermission) {
      routeElement = <PermissionRoute viewPermission={viewPermission}>{routeElement}</PermissionRoute>;
    }

    if (betaFeature) {
      routeElement = <BetaFeatureRoute betaFeature={betaFeature}>{routeElement}</BetaFeatureRoute>;
    }

    if (getDefaultPath) {
      const redirectRoute = {
        index: true,
        element: <DefaultPathRoute getDefaultPath={getDefaultPath} menuItems={children ?? []} />,
        errorElement: pageErrorElement,
      };

      return {
        path,
        element: routeElement,
        errorElement: routeErrorElement,
        children: [redirectRoute, ...(children ? transformRoutes(children, true, betaFeature) : [])],
      };
    }

    const routeChildren = children ? transformRoutes(children, true, betaFeature) : [];
    if (path && routeElement) {
      return {
        path,
        element: routeElement,
        errorElement: routeErrorElement,
        children: routeChildren,
      };
    } else {
      return routeChildren;
    }
  });
};

const router = createBrowserRouter(transformRoutes(rootRoutes));

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
